// Вопросы к матчу (§2.18 этап 2, крикет; восемь рынков с HQ-2312): массив questions[] блока ОДНОГО матча
// (fixtures/{id}/ и ответ PUT predictions/{id}/). Вопроса нет — не рисуем; нет массива — блока нет. Порядок — сервера.
// Рисуем по state сервера (open | locked | awaiting_result | settled | void); старый ответ без state — из open и status.
// Ответ — PUT {market, pick}; сменить можно, пока state = open; повторный тап по своему — снять (DELETE ?market=).
// Доли (percent) до своего ответа сервер не отдаёт (null) — долей нет. note — строка сервера под вопросом,
// result.title — итог, line — линия больше/меньше. Длинные списки игроков (бэтсмен, боулер, игрок матча) свёрнуты.
// Выбор виден сразу (пока идёт PUT); сбой — выбор возвращается к ответу сервера, под вопросом ошибка и «Повторить».
// HQ-2343: обучение мира подсвечивает первый открытый вопрос — attrs ставит на него якорь, onAnswered — ответ принят.
import { useState } from 'react';
import { ApiError } from '../api/client';
import { useAnswer } from '../api/gameQueries';
import type { GameFixture, GameQuestion, GameQuestionOption, GameQuestionState } from '../api/gameTypes';
import { t } from '../i18n/i18n';
import { haptic } from '../telegram/webapp';
import pc from './PredictionCard.module.css';
import styles from './MatchQuestions.module.css';

/** Сколько игроков видно в свёрнутом списке (свой ответ — всегда виден). */
export const COLLAPSED = 4;

/** Видимые варианты: всё — если список короткий или развёрнут; иначе первые COLLAPSED + свой ответ. */
export function visibleOptions(q: GameQuestion, expanded: boolean): GameQuestionOption[] {
  if (expanded || q.options.length <= COLLAPSED + 1) return q.options;
  const head = q.options.slice(0, COLLAPSED);
  const mine = q.options.find((o) => o.pick === q.my_pick);
  return mine && !head.includes(mine) ? [mine, ...head] : head;
}

const SETTLED_STATUS = /^(won|lost|void)$/;

/** Состояние вопроса: state сервера; старый ответ без него — open → open, рассчитанный status → settled/void, иначе
 *  закрыт. Незнакомое значение state — как закрытый вопрос (кнопки неактивны). */
export function questionState(q: GameQuestion): GameQuestionState {
  const known: GameQuestionState[] = ['open', 'locked', 'awaiting_result', 'settled', 'void'];
  if (q.state) return known.includes(q.state as GameQuestionState) ? q.state as GameQuestionState : 'locked';
  if (q.open) return 'open';
  if (q.status === 'won' || q.status === 'lost') return 'settled';
  return q.status === 'void' ? 'void' : 'locked';
}

/** Линию показываем строкой, если её нет в заголовке вопроса (у innings_runs сервер пишет её в заголовке). */
export function lineLabel(q: GameQuestion): string | null {
  if (q.line === null || q.line === undefined || q.title.includes(String(q.line))) return null;
  return t('tg_game_q_line', String(q.line));
}

type Answer = { market: string; pick: string | null };

/** Первый открытый вопрос (на него обучение ставит якорь); открытых нет — null. */
export const firstOpenQuestion = (questions: GameQuestion[] | undefined) =>
  questions?.find((q) => questionState(q) === 'open' && q.options.length > 0) ?? null;

export function MatchQuestions({ fixture, firstOpenAttrs, onAnswered }: {
  fixture: GameFixture; firstOpenAttrs?: Record<string, string>; onAnswered?: (market: string) => void;
}) {
  const answer = useAnswer(fixture.fixture_id);
  const questions = fixture.questions;
  if (!questions?.length) return null;
  const first = firstOpenQuestion(questions);
  const sent = answer.variables;
  const onPick = (market: string, pick: string | null) => {
    haptic.impact();
    answer.mutate({ market, pick }, { onSuccess: () => { if (pick !== null) onAnswered?.(market); } });
  };
  // 409 prediction_closed: вопрос уже закрыт, блок перечитывается — повторять нечего
  const retryable = !(answer.error instanceof ApiError && answer.error.code === 'prediction_closed');
  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <span className={styles.title}>{t('tg_game_questions')}</span>
      </div>
      {questions.map((q) => (
        <Question key={q.market} q={q} pending={answer.isPending}
          sending={answer.isPending && sent?.market === q.market ? sent : undefined}
          failed={answer.isError && sent?.market === q.market}
          onRetry={retryable && sent ? () => onPick(sent.market, sent.pick) : undefined}
          onPick={(pick) => onPick(q.market, pick)}
          attrs={q === first ? firstOpenAttrs : undefined} />
      ))}
    </div>
  );
}

function Question({ q: server, pending, sending, failed, onRetry, onPick, attrs }: {
  q: GameQuestion; pending: boolean; sending?: Answer; failed: boolean; onRetry?: () => void;
  onPick: (pick: string | null) => void; attrs?: Record<string, string>;
}) {
  const [expanded, setExpanded] = useState(false);
  // пока идёт PUT/DELETE — выбор человека; ответ сервера или сбой возвращают его выбор сервера
  const q = sending ? { ...server, my_pick: sending.pick } : server;
  const state = questionState(server);
  const players = q.options.some((o) => o.player);
  // пара (жребий, больше/меньше, да/нет) — в два столбца; три варианта (кто больше шестёрок) и игроки — столбцом
  const column = players || q.options.length !== 2;
  const shown = visibleOptions(q, expanded);
  const line = lineLabel(q);
  return (
    <div className={styles.question} data-market={q.market} data-state={state} {...attrs}>
      <div className={styles.qHead}>
        <span className={styles.qTitle}>{q.title}</span>
        <span className={`score ${styles.points}`}>{`+${q.points}`}</span>
      </div>
      {line && <div className={`score ${styles.line}`}>{line}</div>}
      <div className={column ? `${styles.players} ${expanded ? styles.scroll : ''}` : styles.pair}>
        {shown.map((o) => {
          const mine = q.my_pick === o.pick;
          const winner = !!q.result?.pick && q.result.pick === o.pick;
          return (
            <button key={o.pick} className={`${pc.option} ${styles.opt} ${mine ? pc.mine : ''} ${winner ? pc.winner : ''}`}
              aria-pressed={mine} disabled={state !== 'open' || pending} onClick={() => onPick(mine ? null : o.pick)}>
              {o.percent !== null && <span className={pc.fill} style={{ width: `${o.percent}%` }} />}
              {o.player && <PlayerFace player={o.player} />}
              <span className={`${pc.name} ${column ? styles.wrapLong : styles.wrapName}`}>{o.title}</span>
              {o.percent !== null ? <span className="score">{Math.round(o.percent)}%</span>
                : !column && <span className={styles.pctSlot} aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      {players && q.options.length > COLLAPSED + 1 && (
        <button className={styles.more} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
          {expanded ? t('tg_game_q_less') : t('tg_game_q_all', q.options_count || q.options.length)}
        </button>
      )}
      {q.note && <div className={styles.note}>{q.note}</div>}
      {q.result?.title && <div className={styles.result}>{t('tg_game_q_result', q.result.title)}</div>}
      <QuestionFooter q={server} />
      {failed && (
        <div className={`${pc.footer} ${styles.failed}`} role="alert">
          <span className={pc.error}>{t('mc_error_title')}</span>
          {onRetry && <button className={styles.retry} disabled={pending} onClick={onRetry}>{t('mc_retry')}</button>}
        </div>
      )}
    </div>
  );
}

function PlayerFace({ player }: { player: NonNullable<GameQuestionOption['player']> }) {
  if (player.image) return <img className={styles.face} src={player.image} alt="" width={28} height={28} loading="lazy" />;
  const words = player.name.split(/\s+/).filter(Boolean);
  const initials = [words[0]?.[0], words.length > 1 ? words[words.length - 1]![0] : ''].join('').toUpperCase();
  return <span className={`${styles.face} ${styles.initials}`} aria-hidden="true">{initials}</span>;
}

/** Итог или состояние вопроса: угадал (+очки), не угадал, не засчитан, мой ответ, закрыт. Строка сервера (note) уже
 *  объясняет ожидание игрока матча и отмену — тогда своё «закрыто» и «не засчитан» не дублируем. */
export function QuestionFooter({ q }: { q: GameQuestion }) {
  const state = questionState(q);
  const parts: string[] = [];
  if (q.status === 'won') parts.push(t('tg_game_q_won', q.points_won ?? q.points));
  else if (q.status === 'lost') parts.push(t('tg_game_lost'));
  else if (q.status === 'void' || (state === 'void' && !q.note)) parts.push(t('tg_game_q_void'));
  else if (q.my_pick_title) parts.push(t('tg_game_q_my', q.my_pick_title));
  if (state === 'open' && q.my_pick && !q.status?.match(SETTLED_STATUS)) parts.push(t('tg_game_withdraw'));
  if ((state === 'locked' || (state === 'awaiting_result' && !q.note)) && !q.status?.match(SETTLED_STATUS)) {
    parts.push(`🔒 ${t('tg_vote_closed')}`);
  }
  if (q.total > 0) parts.push(q.total === 1 ? t('tg_votes_one') : t('tg_votes', q.total));
  if (!parts.length) return null;
  return (
    <div className={`${pc.footer} ${q.status === 'won' ? styles.won : ''}`}>
      {parts.map((p) => <span key={p}>{p}</span>)}
    </div>
  );
}
